import { CancelToken } from 'axios';
import { TFunction } from 'i18next';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CompanySegment, Segment, SegmentType, getCompanySegment } from '~src/interfaces/segment.interface';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';

import { Scale, ScaleIncludeParam, ScaleVisualization } from '~src/interfaces/scale.interface';
import { ContentLocalesI } from '~src/interfaces/session.types';
import { Topic } from '~src/interfaces/topic.interface';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { RichCycle } from '~src/modules/spidergraph/types';
import SegmentsSelect from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { StoreI } from '~src/rootReducer';
import { defaultBaseApi } from '~src/services/ApiController';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { translateContent } from '~src/services/translationServices';
import CheckBox from '~src/shared/form/Checkbox';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import SearchListSelector from '~src/shared/selectors/searchListSelector/SearchListSelector';
import {
  FilterCol,
  FilterDescription,
  FilterInput,
  FilterTitle,
  FilterTitleAndDescription,
  FiltersContainer,
  FiltersRow,
  SelectorStep,
} from './spiderGraphFilterDetails.sc';

interface Props {
  onAboutSegmentsChange: (segments: Segment[]) => void;
  onAskSegmentsChange: (segments: Segment[]) => void;
  onTopicChange: (topic?: Topic) => void;
  initialAboutSegments: string[];
  initialAskSegments: string[];
  selectedTopic?: string;
  onCyclesChange: (cycles: RichCycle[]) => void;
  selectedCycles: RichCycle[];
  availableCycles: RichCycle[];
  availableTopics: Topic[];
  onSelfAssessmentChange: (isIncluded: boolean) => void;
  isSelfAssessmentIncluded: boolean;
  surveySubjectType: SurveySubjectTypes;
  activeStepIndex: number;
}

function formatDay(isoDateString: string) {
  return isoDateString.split('T').shift();
}

export function displayCycleString(
  t: TFunction,
  cycle: { start_date: string; due_date: string | null; closed: boolean; name?: ContentLocalesI },
): string {
  const closedDesc = cycle.closed ? t('x.cycle.closed') : t('x.cycle.open');
  const cycleName = translateContent(cycle.name ?? {});

  return (
    cycleName ||
    `${formatDay(cycle.start_date)} ${
      cycle.due_date ? ' – ' + formatDay(cycle.due_date) : ''
    } (${closedDesc})`
  );
}

function richCycleToSearchListSelectorItem(t: TFunction, richCycle: RichCycle): OptionItem {
  return {
    id: richCycle.getId(),
    name: displayCycleString(t, richCycle.cycle),
  };
}

function selectorItemsToRichCycle(selectorItemId: string, richCycles: RichCycle[]): RichCycle {
  const cycle = richCycles.find(cycle => cycle.getId() === selectorItemId);
  if (!cycle) {
    throw new Error('logic error: selector must not choose from non-available RichCycle');
  }
  return cycle;
}

function toSearchableBits(str: string): string[] {
  return str
    .toLocaleLowerCase()
    .split(/\s/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

// could this be a query for all answers (… that should return the company segment?
export function isAllAnswersQuery(allAnswersLabel: string, query: string): boolean {
  const labelBits = toSearchableBits(allAnswersLabel);
  const queryBits = toSearchableBits(query);
  return labelBits.some(l => queryBits.some(q => l.indexOf(q) === 0));
}

const LinesSelector: FC<Props> = (props) => {
  const { t } = useTranslation();
  const noneAsAllOption: OptionItem = { name: t('x.analysis.header.filters.none-as-all'), id: 'NONE' };

  const [selectedAboutSegments, setSelectedAboutSegments] = useState<Segment[]>([]);
  const [selectedAskSegments, setSelectedAskSegments] = useState<Segment[]>([]);
  const [managerSegments, setManagerSegments] = useState<Segment[]>([]);
  const [isDirectReportsEnabled, setIsDirectReportsEnabled] = useState(false);
  // We want to reveal part of the form only when user has taken certain actions
  const [isPeopleSurveyInitialized, setIsPeopleSurveyInitialized] = useState(false);

  const company = useSelector<StoreI, CompanySegment | undefined>((s: StoreI) => {
    return (s.user.session.company && getCompanySegment(s.user.session.company)) || undefined;
  });

  const isAskSegmentEnabled = useSelector<StoreI, boolean>((s: StoreI) => {
    return Boolean(
      s.user.session.company && s.user.session.company.features.survey?.spiderGraphGroupByRespondents,
    );
  });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchScales({ include: ScaleIncludeParam.ScaleVersion }));
  }, []);

  const scales = useSelector<StoreI, Record<string, Scale>>(s => s.scales?.byId);

  function topicToSearchListSelectorItem(topic: Topic): OptionItem {
    const scale = scales[topic.scaleVersionId];
    return {
      id: topic.id,
      name: translateContent(topic.name),
      disabled: scale?.visualization === ScaleVisualization.McqOptions,
    };
  }

  const onAboutSegmentsChange = (segments: Segment[]) => {
    props.onAboutSegmentsChange(segments);

    if (props.surveySubjectType !== SurveySubjectTypes.People) return;

    if (!isPeopleSurveyInitialized) setIsPeopleSurveyInitialized(true);

    const uuids = segments.map(seg => seg.id);

    defaultBaseApi
      .getTeamSegments(uuids)
      .then((response) => {
        const segments = Object.values(response.data);
        setManagerSegments(segments);
      })
      .catch((err) => {
        console.log('Error occurred during fetching team-segments', err);
        throw err; // I guess too
      });
  };

  useEffect(() => {
    if (props.initialAboutSegments.length) {
      defaultBaseApi
        .getSegments({ uuids: props.initialAboutSegments })
        .then((segmentsResponse) => {
          setSelectedAboutSegments(segmentsResponse.data.filter(s => s.user_count));
          onAboutSegmentsChange(segmentsResponse.data);
        })
        .catch((e) => {
          throw e; // I guess
        });
    }
    if (
      props.surveySubjectType === SurveySubjectTypes.People &&
      isAskSegmentEnabled &&
      props.initialAskSegments.length
    ) {
      defaultBaseApi
        .getSegments({ uuids: props.initialAskSegments })
        .then((segmentsResponse) => {
          const eligibleSegments = segmentsResponse.data.filter(s => s.user_count);
          eligibleSegments.forEach(changeCompanyName);
          setSelectedAskSegments(eligibleSegments);

          props.onAskSegmentsChange(segmentsResponse.data);
        })
        .catch((e) => {
          throw e; // I guess
        });
    }
  }, []);

  useEffect(() => {
    let newAskSegments: Segment[] = [];

    if (isAskSegmentEnabled) {
      if (isDirectReportsEnabled) {
        newAskSegments = [...managerSegments, ...selectedAskSegments];
      } else {
        newAskSegments = selectedAskSegments;
      }
    } else {
      if (isDirectReportsEnabled) {
        newAskSegments = managerSegments;
      }
    }

    onAskSegmentsChange(newAskSegments);
  }, [isDirectReportsEnabled, managerSegments.length]);

  const changeCompanyName = (s: Segment): void => {
    if (s.type === SegmentType.Company) {
      s.meta.name = t('x.spidergraph.company-segment-name');
    }
  };

  const fetchCycleSuggestions = () => {
    return Promise.resolve(props.availableCycles);
  };

  const fetchTopicSuggestions = () => {
    return Promise.resolve(props.availableTopics);
  };

  const onCyclesChange = (optionItemIds: string[]) => {
    if (!Array.isArray(optionItemIds)) {
      return; // TODO fuck that
    }
    const selectedCycles = optionItemIds.map(id => selectorItemsToRichCycle(id, props.availableCycles));
    props.onCyclesChange(selectedCycles);
  };

  const onTopicChange = (optionItem: OptionItem) => {
    props.onTopicChange(props.availableTopics.find(t => t.id === optionItem.id));
  };

  const onSelfAssessmentChange = (isChecked: boolean) => {
    props.onSelfAssessmentChange(isChecked);
  };

  const handleShowDirectReportsChange = (isVisible: boolean) => {
    setIsDirectReportsEnabled(isVisible);
  };

  const onAskSegmentsChange = (segments: Segment[]) => {
    if (segments.length === 0 && isAskSegmentEnabled && company) {
      segments = [company];
    }
    props.onAskSegmentsChange(segments);
  };

  const initialTopic = props.availableTopics.find(t => t.id === props.selectedTopic);
  let initialTopicOption: OptionItem = initialTopic
    ? topicToSearchListSelectorItem(initialTopic)
    : noneAsAllOption;

  if (initialTopicOption.disabled) {
    initialTopicOption = noneAsAllOption;
  }

  const withDeactivatedSegments = async (value: string, cancelToken: CancelToken) => {
    const segments = await fetchSearchSegments(
      value,
      cancelToken,
      [SegmentType.Company, SegmentType.Group, SegmentType.User],
      true,
    );

    return segments.filter(s => s.user_count && s.user_count > 0);
  };

  const withDeactivatedAskSegments = async (value: string, cancelToken: CancelToken) => {
    const segments = await withDeactivatedSegments(value, cancelToken);

    if (
      company &&
      //      isAllAnswersQuery(t('x.spidergraph.company-segment-name'), value) &&
      !segments.find(s => s.type === SegmentType.Company)
    ) {
      segments.push(company);
    }

    segments.forEach(changeCompanyName);

    return segments;
  };

  const oneAnswerOptionScaleIds = Object.values(scales || {})
    ?.map((scale) => {
      return scale?.labels?.length === 1 ? scale.versionId : undefined;
    })
    .filter(el => el !== undefined);

  const filteredAvailableTopics = props.availableTopics?.filter(
    item => !oneAnswerOptionScaleIds?.includes(item.scaleVersionId),
  );

  const segmentLine = ({ step }: { step: number }) => (
    <FiltersContainer className={props.activeStepIndex === 1 ? 'highlighted' : ''} key={step}>
      <FilterCol>
        <SelectorStep>{step}</SelectorStep>
      </FilterCol>
      <FiltersRow>
        <FilterTitleAndDescription>
          <FilterTitle
            data-translation={
              props.surveySubjectType === SurveySubjectTypes.People
                ? 'x.analysis.header.filters.segment.title'
                : 'x.analysis.header.filters.group-segment.title'
            }
          >
            {t(
              props.surveySubjectType === SurveySubjectTypes.People
                ? 'x.analysis.header.filters.segment.title'
                : 'x.analysis.header.filters.group-segment.title',
            )}
          </FilterTitle>
          <FilterDescription
            data-translation={
              props.surveySubjectType === SurveySubjectTypes.People
                ? 'x.spider_graph.header.filters.segment.description'
                : 'x.spider_graph.header.filters.group-segment.description'
            }
          >
            {t(
              props.surveySubjectType === SurveySubjectTypes.People
                ? 'x.spider_graph.header.filters.segment.description'
                : 'x.spider_graph.header.filters.group-segment.description',
            )}
          </FilterDescription>
        </FilterTitleAndDescription>
        <FilterInput>
          <SegmentsSelect
            errorTxt={props.initialAboutSegments.length ? '' : 'analysis.spider-web.no-segment-selected'}
            fetchSuggestions={withDeactivatedSegments}
            chipsName="segments"
            selectedPeople={selectedAboutSegments}
            onChangePeople={onAboutSegmentsChange}
          />
        </FilterInput>
      </FiltersRow>
    </FiltersContainer>
  );

  const aboutSegmentLine = ({ step }: { step: number }) => (
    <FiltersContainer className={props.activeStepIndex === 1 ? 'highlighted' : ''} key={step}>
      <FilterCol>
        <SelectorStep>{step}</SelectorStep>
      </FilterCol>
      <FiltersRow>
        <FilterTitleAndDescription>
          <FilterTitle data-translation="x.analysis.header.filters.about-segment.title">
            {t('x.analysis.header.filters.about-segment.title')}
          </FilterTitle>
          <FilterDescription data-translation="x.spider_graph.header.filters.about-segment.description">
            {t('x.spider_graph.header.filters.about-segment.description')}
          </FilterDescription>
        </FilterTitleAndDescription>

        <FilterInput>
          <SegmentsSelect
            fetchSuggestions={withDeactivatedSegments}
            chipsName="segments"
            selectedPeople={selectedAboutSegments}
            onChangePeople={onAboutSegmentsChange}
          />
        </FilterInput>
      </FiltersRow>
    </FiltersContainer>
  );

  const selfAssessmentLine = ({ step }: { step: number }) => (
    <FiltersContainer key={step}>
      <FilterCol>
        <SelectorStep>{step}</SelectorStep>
      </FilterCol>
      <FiltersRow>
        <FilterTitleAndDescription>
          <FilterTitle data-translation="x.analysis.header.filters.three_sixty_.comparison">
            {t('x.analysis.header.filters.three_sixty_.comparison')}
          </FilterTitle>
          <FilterDescription data-translation="x.spider_graph.header.filters.three_sixty_.description">
            {t('x.spider_graph.header.filters.three_sixty_.description')}
          </FilterDescription>
        </FilterTitleAndDescription>
        <FilterInput $isFixWidth>
          <CheckBox
            displayIconName={'iconPersonOutline'}
            onChange={onSelfAssessmentChange}
            id="self-assessment"
            labelKey="x.analysis.header.filters.selfAssessment"
            label={t('x.analysis.header.filters.selfAssessment')}
            isChecked={props.isSelfAssessmentIncluded}
          />
        </FilterInput>
        <FilterInput>
          <CheckBox
            displayIconName={'iconPeople'}
            onChange={handleShowDirectReportsChange}
            id="x.analysis.header.filters.showDirectReports"
            labelKey="x.analysis.header.filters.showDirectReports"
            label={t('x.analysis.header.filters.showDirectReports')}
            isChecked={isDirectReportsEnabled}
          />
        </FilterInput>
      </FiltersRow>
    </FiltersContainer>
  );

  const askSegmentsLine = ({ step }: { step: number }) => (
    <FiltersContainer key={step}>
      <FilterCol>
        <SelectorStep>{step}</SelectorStep>
      </FilterCol>
      <FiltersRow>
        <FilterTitleAndDescription>
          <FilterTitle data-translation="x.analysis.header.filters.ask-segment.title">
            {t('x.analysis.header.filters.ask-segment.title')}
          </FilterTitle>
          <FilterDescription data-translation="x.spider_graph.header.filters.ask-segment.description">
            {t('x.spider_graph.header.filters.ask-segment.description')}
          </FilterDescription>
        </FilterTitleAndDescription>

        <FilterInput>
          <SegmentsSelect
            errorTxt={props.initialAskSegments.length ? '' : 'analysis.spider-web.no-segment-selected'}
            fetchSuggestions={withDeactivatedAskSegments}
            chipsName="ask-segments"
            selectedPeople={selectedAskSegments}
            onChangePeople={onAskSegmentsChange}
          />
        </FilterInput>
      </FiltersRow>
    </FiltersContainer>
  );

  const cyclesLine = ({ step }: { step: number }) => (
    <FiltersContainer key={step}>
      <FilterCol>
        <SelectorStep>{step}</SelectorStep>
      </FilterCol>
      <FiltersRow>
        <FilterTitleAndDescription>
          <FilterTitle data-translation="x.analysis.header.filters.cycle.title">
            {t('x.analysis.header.filters.cycle.title')}
          </FilterTitle>
          <FilterDescription data-translation="x.spider_graph.header.filters.cycle.description">
            {t('x.spider_graph.header.filters.cycle.description')}
          </FilterDescription>
        </FilterTitleAndDescription>
        <FilterInput>
          <SearchListSelector<string[]>
            fetchItems={fetchCycleSuggestions}
            initialOpt={props.selectedCycles.map(r => r.getId())}
            loadingSearch={false}
            multiSelect
            noMoreItems
            nothingFoundKey=""
            onSelect={onCyclesChange}
            optionsList={props.availableCycles.map(cycle => richCycleToSearchListSelectorItem(t, cycle))}
          />
        </FilterInput>
      </FiltersRow>
    </FiltersContainer>
  );

  const topicsLine = ({ step }: { step: number }) => (
    <FiltersContainer
      className={
        (
          props.surveySubjectType === SurveySubjectTypes.People
            ? props.activeStepIndex === 5
            : props.activeStepIndex === 3
        )
          ? 'highlighted'
          : ''
      }
      key={step}
    >
      <FilterCol>
        <SelectorStep>{step}</SelectorStep>
      </FilterCol>
      <FiltersRow>
        <FilterTitleAndDescription>
          <FilterTitle data-translation="x.analysis.header.filters.topic.title">
            {t('x.analysis.header.filters.topic.title')}
          </FilterTitle>
          <FilterDescription data-translation="x.spider_graph.header.filters.topic.description">
            {t('x.spider_graph.header.filters.topic.description')}
          </FilterDescription>
        </FilterTitleAndDescription>
        <FilterInput>
          <SearchListSelector
            fetchItems={fetchTopicSuggestions}
            initialOpt={initialTopicOption}
            loadingSearch={false}
            maxWidth={'initial'}
            noMoreItems
            noSearch
            nothingFoundKey=""
            onSelect={onTopicChange}
            optionsList={[noneAsAllOption].concat(filteredAvailableTopics.map(topicToSearchListSelectorItem))}
          />
        </FilterInput>
      </FiltersRow>
    </FiltersContainer>
  );

  let lines = [segmentLine, cyclesLine, topicsLine];

  if (props.surveySubjectType === SurveySubjectTypes.People) {
    lines = [aboutSegmentLine];

    if (isPeopleSurveyInitialized) {
      lines.push(selfAssessmentLine);

      if (isAskSegmentEnabled) {
        lines.push(askSegmentsLine);
      }

      lines.push(cyclesLine, topicsLine);
    }
  }

  return <>{lines.map((Line, index) => Line({ step: index + 2 }))}</>;
};

export default LinesSelector;
